import React, { memo, useCallback, useEffect, useId, useRef, useState } from "react";

/**
 * ChainFlow OS · PremiumRange
 * ────────────────────────────────────────────────────────────
 * The standard continuous-selection primitive. Where `PremiumStepper`
 * handles discrete, precision numeric interaction (press-and-hold
 * acceleration), `PremiumRange` handles continuous sweep — risk band
 * thresholds, completion confidence cut-offs, forecasting tolerance,
 * intervention sensitivity, revenue exposure filters, Director Centre
 * ranges, Mission Control filtering, chain fragility tuning, and any
 * future operational range surface.
 *
 * Locked v1 interaction grammar (do not tune per-consumer):
 *  • Pointer Events for mouse + touch parity. `touch-action: none` on
 *    the track so vertical scrolls don't hijack horizontal drags.
 *  • Click-on-track snaps thumb to that position (operational precision).
 *  • Drag thumb for fine adjustment.
 *  • Keyboard (focused thumb):
 *       ← / ↓  : −1 step
 *       → / ↑  : +1 step
 *       PgDown : −10 × step
 *       PgUp   : +10 × step
 *       Home   : lo bound
 *       End    : hi bound
 *  • aria-valuemin / aria-valuemax / aria-valuenow / role="slider".
 *  • Snap to nearest step at every set.
 *  • Disabled state shipped first-class (matches PremiumStepper).
 *
 * Locked v1 visual identity (matches the operational rhythm of
 * `PremiumStepper`):
 *  • Slate-100 rounded track, height 6 / 8 / 10 depending on size.
 *  • Indigo → violet active fill (the same brand accent used across the
 *    operational forecasting and revenue surfaces).
 *  • White circular thumb with violet focus ring + soft shadow.
 *  • Subtle 120 ms thumb scale on press; suppressed under
 *    `prefers-reduced-motion`.
 *  • Optional tick marks for operational thresholds (e.g. 20 / 50 / 80).
 *  • Optional inline value badge with `tabular-nums` so digits don't
 *    shimmer width as the value updates.
 *
 * @prop {number} value
 * @prop {(next:number) => void} onChange
 * @prop {number} lo
 * @prop {number} hi
 * @prop {number} [step=1]
 * @prop {string} [prefix]                e.g. "£"
 * @prop {string} [suffix]                e.g. "%", " days"
 * @prop {string} [label]                 Operational label rendered above the track.
 * @prop {string} [hint]                  Calm secondary line under the label.
 * @prop {(v:number) => string} [format]  Custom value formatter.
 * @prop {boolean} [showValue=true]       Show the inline value badge.
 * @prop {number[]} [ticks]               Tick positions (in value space).
 * @prop {"sm"|"md"|"lg"} [size="md"]
 * @prop {boolean} [disabled=false]
 * @prop {string} [testid]                Yields testid, -track, -thumb, -value.
 * @prop {string} [className]
 */
export const PremiumRange = memo(function PremiumRange({
  value,
  onChange,
  lo,
  hi,
  step = 1,
  prefix = "",
  suffix = "",
  label,
  hint,
  format,
  showValue = true,
  ticks,
  size = "md",
  disabled = false,
  testid,
  className = "",
}) {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const [pressed, setPressed] = useState(false);
  const labelId = useId();
  const hintId = useId();

  // Defensive snap so consumer values stay on-grid even if external state
  // is set off-step.
  const snap = useCallback((v) => {
    const clamped = Math.max(lo, Math.min(hi, v));
    const snapped = Math.round((clamped - lo) / step) * step + lo;
    return Math.max(lo, Math.min(hi, snapped));
  }, [lo, hi, step]);

  // Convert a clientX to a value snapped to the step grid.
  const valueFromClientX = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return valueRef.current;
    const rect = el.getBoundingClientRect();
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    const raw = lo + Math.max(0, Math.min(1, ratio)) * (hi - lo);
    return snap(raw);
  }, [lo, hi, snap]);

  const setIfChanged = useCallback((next) => {
    if (next !== valueRef.current) onChange(next);
  }, [onChange]);

  // ─── Pointer interaction (touch + mouse parity) ───
  const onPointerDown = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    const el = trackRef.current;
    if (el && el.setPointerCapture && e.pointerId != null) {
      try { el.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }
    draggingRef.current = true;
    setPressed(true);
    setIfChanged(valueFromClientX(e.clientX));
  }, [disabled, setIfChanged, valueFromClientX]);

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    setIfChanged(valueFromClientX(e.clientX));
  }, [setIfChanged, valueFromClientX]);

  const endDrag = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setPressed(false);
  }, []);

  // ─── Keyboard interaction (operational precision) ───
  const onKeyDown = useCallback((e) => {
    if (disabled) return;
    const range = hi - lo;
    const big = Math.max(step, Math.round(range / 10));
    let next = valueRef.current;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown": next = snap(valueRef.current - step); break;
      case "ArrowRight":
      case "ArrowUp":   next = snap(valueRef.current + step); break;
      case "PageDown":  next = snap(valueRef.current - big);  break;
      case "PageUp":    next = snap(valueRef.current + big);  break;
      case "Home":      next = lo; break;
      case "End":       next = hi; break;
      default: return;
    }
    e.preventDefault();
    setIfChanged(next);
  }, [disabled, hi, lo, snap, step, setIfChanged]);

  // Defensive: if value drifts off-step from props, snap it once.
  useEffect(() => {
    const snapped = snap(value);
    if (snapped !== value) onChange(snapped);
    // We intentionally don't depend on onChange (consumer-stable expected).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, snap]);

  const ratio = hi === lo ? 0 : (value - lo) / (hi - lo);
  const pct = Math.max(0, Math.min(100, ratio * 100));

  const sizing = {
    sm: { track: "h-1.5", thumb: "w-3.5 h-3.5", value: "text-[11.5px]" },
    md: { track: "h-2",   thumb: "w-4 h-4",     value: "text-[12.5px]" },
    lg: { track: "h-2.5", thumb: "w-5 h-5",     value: "text-[13px]" },
  }[size] || { track: "h-2", thumb: "w-4 h-4", value: "text-[12.5px]" };

  const display = format ? format(value) : `${prefix}${value.toLocaleString()}${suffix}`;

  const labelledById = label ? labelId : undefined;
  const describedById = hint ? hintId : undefined;

  return (
    <div
      className={`cf-pr ${disabled ? "cf-pr--disabled" : ""} ${className}`}
      data-testid={testid ? `${testid}-wrap` : undefined}
    >
      {(label || (showValue && !label)) && (
        <div className="flex items-baseline justify-between gap-3 mb-1.5">
          {label ? (
            <span
              id={labelId}
              className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500"
            >
              {label}
            </span>
          ) : <span />}
          {showValue && (
            <span
              className={`font-display font-bold text-slate-900 tabular-nums ${sizing.value}`}
              data-testid={testid}
            >
              {display}
            </span>
          )}
        </div>
      )}
      {hint && (
        <p id={hintId} className="text-[11px] text-slate-500 leading-snug mb-2">
          {hint}
        </p>
      )}
      <div
        ref={trackRef}
        className={`cf-pr-track relative w-full ${sizing.track} rounded-full bg-slate-100 cursor-pointer`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{ touchAction: "none" }}
        data-testid={testid ? `${testid}-track` : undefined}
      >
        {/* Active fill */}
        <div
          className="absolute inset-y-0 left-0 rounded-full cf-pr-fill"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, #4F46E5 0%, #7C3AED 100%)",
          }}
        />
        {/* Tick marks (optional, drawn as small slate dots above the track) */}
        {Array.isArray(ticks) && ticks.length > 0 && (
          <div className="pointer-events-none absolute inset-0">
            {ticks.map((t) => {
              const tPct = hi === lo ? 0 : ((t - lo) / (hi - lo)) * 100;
              const reached = value >= t;
              return (
                <span
                  key={t}
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-[3px] h-[3px] rounded-full"
                  style={{
                    left: `${tPct}%`,
                    background: reached ? "rgba(255,255,255,0.95)" : "rgba(15,23,42,0.25)",
                  }}
                />
              );
            })}
          </div>
        )}
        {/* Thumb */}
        <button
          type="button"
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-valuemin={lo}
          aria-valuemax={hi}
          aria-valuenow={value}
          aria-valuetext={display}
          aria-labelledby={labelledById}
          aria-describedby={describedById}
          aria-disabled={disabled || undefined}
          aria-orientation="horizontal"
          onKeyDown={onKeyDown}
          data-testid={testid ? `${testid}-thumb` : undefined}
          className={
            `cf-pr-thumb absolute top-1/2 -translate-y-1/2 -translate-x-1/2 ` +
            `${sizing.thumb} rounded-full bg-white border border-slate-300 ` +
            `focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70 ` +
            `${pressed ? "cf-pr-thumb--pressed" : ""}`
          }
          style={{
            left: `${pct}%`,
            boxShadow:
              "0 1px 2px rgba(15,23,42,0.10), 0 4px 12px -3px rgba(79,70,229,0.18)",
          }}
        />
      </div>
      <style>{`
        .cf-pr-track { transition: background-color 180ms ease; }
        .cf-pr-thumb {
          transition: transform 120ms cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 180ms ease, border-color 180ms ease;
          will-change: transform;
        }
        .cf-pr-thumb:hover { border-color: rgba(124,58,237,0.45); }
        .cf-pr-thumb--pressed {
          transform: translate(-50%, -50%) scale(1.12);
          border-color: rgba(124,58,237,0.55);
          box-shadow: 0 1px 2px rgba(15,23,42,0.10),
                      0 6px 16px -2px rgba(124,58,237,0.30),
                      0 0 0 4px rgba(124,58,237,0.10);
        }
        .cf-pr-fill { transition: width 90ms linear; }
        .cf-pr--disabled .cf-pr-track { cursor: not-allowed; opacity: 0.55; }
        .cf-pr--disabled .cf-pr-thumb { cursor: not-allowed; }
        @media (prefers-reduced-motion: reduce) {
          .cf-pr-thumb, .cf-pr-fill, .cf-pr-track { transition: none !important; }
          .cf-pr-thumb--pressed { transform: translate(-50%, -50%); }
        }
      `}</style>
    </div>
  );
});

export default PremiumRange;
